/**
 * Phone number validation regex patterns
 * Supporting international formats and country-specific rules
 */

// Simplified phone number patterns (current default) - excludes + prefix for country selector usage
export const PHONE_SIMPLE_REGEX = /^[0-9]+$/; // Only numbers (no + prefix, handled by country selector)
export const PHONE_SIMPLE_MIN_LENGTH = 6; // Adjusted for international usage without prefix
export const PHONE_SIMPLE_MAX_LENGTH = 11; // Adjusted for international usage without prefix

// Strict phone number patterns (versioned for future use)
export const PHONE_BASE_REGEX_STRICT = /^(\+)?[0-9\s\-\(\)\.]+$/;
export const PHONE_DIGITS_ONLY_REGEX_STRICT = /^\+?[0-9]+$/;
export const PHONE_INTERNATIONAL_REGEX_STRICT = /^\+[1-9]\d{1,14}$/;

// Legacy exports (for backward compatibility - now point to simple validation)
export const PHONE_BASE_REGEX = PHONE_SIMPLE_REGEX;
export const PHONE_DIGITS_ONLY_REGEX = PHONE_SIMPLE_REGEX;
export const PHONE_INTERNATIONAL_REGEX = PHONE_SIMPLE_REGEX;

// Country-specific phone patterns (strict validation - versioned for future use)
export const PHONE_PATTERNS_STRICT = {
    IT: /^(\+39)?[0-9]{6,11}$/, // Italy: +39 followed by 6-11 digits
    US: /^(\+1)?[2-9]\d{2}[2-9]\d{2}\d{4}$/, // US: +1 followed by 10 digits
    GB: /^(\+44)?[1-9]\d{8,9}$/, // UK: +44 followed by 9-10 digits
    FR: /^(\+33)?[1-9]\d{8}$/, // France: +33 followed by 9 digits
    DE: /^(\+49)?[1-9]\d{10,11}$/, // Germany: +49 followed by 11-12 digits
    AU: /^(\+61)?[2-9]\d{8}$/, // Australia: +61 followed by 9 digits
    SG: /^(\+65)?[6-9]\d{7}$/, // Singapore: +65 followed by 8 digits starting with 6-9
    HK: /^(\+852)?[2-9]\d{7}$/ // Hong Kong: +852 followed by 8 digits starting with 2-9
};

// Legacy export (for backward compatibility)
export const PHONE_PATTERNS = PHONE_PATTERNS_STRICT;

// Phone validation constraints
export const PHONE_MIN_LENGTH = PHONE_SIMPLE_MIN_LENGTH;
export const PHONE_MAX_LENGTH = PHONE_SIMPLE_MAX_LENGTH;
export const PHONE_MIN_DIGITS = 6; // Kept for backward compatibility

/**
 * Simplified phone number validation (current default)
 * Accepts only numbers, length 6-11 characters (excludes country prefix)
 */
export function validatePhoneNumber(
    phone: string
): {
    isValid: boolean;
    errors: string[];
} {
    const errors: string[] = [];
    const trimmedPhone = phone.trim();

    // Basic length check
    if (trimmedPhone.length < PHONE_SIMPLE_MIN_LENGTH) {
        errors.push(`Phone number must be at least ${PHONE_SIMPLE_MIN_LENGTH} digits`);
    }

    if (trimmedPhone.length > PHONE_SIMPLE_MAX_LENGTH) {
        errors.push(`Phone number cannot exceed ${PHONE_SIMPLE_MAX_LENGTH} digits`);
    }

    // Simple format check - only numbers (no + prefix, handled separately by country selector)
    if (!PHONE_SIMPLE_REGEX.test(trimmedPhone)) {
        errors.push('Phone number can only contain numbers');
    }

    return {
        isValid: errors.length === 0,
        errors
    };
}

/**
 * Strict phone number validation (versioned for future use)
 * Supports country-specific patterns and international formats
 */
export function validatePhoneNumberStrict(
    phone: string,
    countryCode?: string
): {
    isValid: boolean;
    errors: string[];
} {
    const errors: string[] = [];
    const trimmedPhone = phone.trim();

    // Basic length check
    if (trimmedPhone.length < PHONE_MIN_LENGTH) {
        errors.push(`Phone number must be at least ${PHONE_MIN_LENGTH} characters`);
    }

    if (trimmedPhone.length > PHONE_MAX_LENGTH) {
        errors.push(`Phone number cannot exceed ${PHONE_MAX_LENGTH} characters`);
    }

    // Basic format check
    if (!PHONE_BASE_REGEX_STRICT.test(trimmedPhone)) {
        errors.push('Phone number contains invalid characters');
    }

    // Extract digits only for further validation
    const digitsOnly = trimmedPhone.replace(/[^\d]/g, '');

    if (digitsOnly.length < PHONE_MIN_DIGITS) {
        errors.push(`Phone number must contain at least ${PHONE_MIN_DIGITS} digits`);
    }

    // Country-specific validation
    if (countryCode && PHONE_PATTERNS_STRICT[countryCode as keyof typeof PHONE_PATTERNS_STRICT]) {
        const countryPattern = PHONE_PATTERNS_STRICT[countryCode as keyof typeof PHONE_PATTERNS_STRICT];
        if (!countryPattern.test(trimmedPhone)) {
            errors.push(`Invalid phone number format for ${countryCode}`);
        }
    }

    return {
        isValid: errors.length === 0,
        errors
    };
}

/**
 * Normalizes phone number format (simplified version)
 */
export function normalizePhoneNumber(phone: string): string {
    // For simplified validation, just ensure it starts with + if it contains only digits
    const trimmedPhone = phone.trim();

    if (PHONE_SIMPLE_REGEX.test(trimmedPhone)) {
        return trimmedPhone.startsWith('+') ? trimmedPhone : `+${trimmedPhone}`;
    }

    return trimmedPhone;
}

/**
 * Normalizes phone number format with country-specific logic (strict version)
 */
export function normalizePhoneNumberStrict(phone: string, countryCode?: string): string {
    const digitsOnly = phone.replace(/[^\d]/g, '');

    switch (countryCode) {
        case 'IT':
            return digitsOnly.startsWith('39') ? `+${digitsOnly}` : `+39${digitsOnly}`;
        case 'US':
            return digitsOnly.startsWith('1') ? `+${digitsOnly}` : `+1${digitsOnly}`;
        case 'GB':
            return digitsOnly.startsWith('44') ? `+${digitsOnly}` : `+44${digitsOnly}`;
        default:
            return phone.startsWith('+') ? phone : `+${digitsOnly}`;
    }
}
